import Button from '@navi/web-ui/lib/primitives/Button/Button';
import { useMemo } from 'react';
import cx from 'classnames';
import PaymentHistoryIcon from 'src/assets/images/icons/PaymentHistoryIcon';
import { STATUS } from '../../constants/EmiSchedule.component';
import {
  EMI_CALENDAR_ACTIONS,
  PAYMENT_HISTORY_ICON_FILL
} from '../../constants/EmiSchedule.constant';
import {
  EmiCalendarActionCellProps,
  EmiFlowType,
  WaiveFeeType
} from '../../interfaces/EmiSchedule.type';
import styles from './index.module.scss';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import {
  CLICKSTREAM_EVENT_NAMES,
  FEE_WAIVER_CLICKSTREAM
} from '../../../../service/clickStream.constant';
import ArrowUpSolidIcon from '@navi/web-ui/lib/icons/ArrowUpSolidIcon';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import { useParams } from 'react-router-dom';
import { EmiFeeActionLabelMap, EmiFeeActions } from './interfaces';
import { createKey } from '@cp/utils/CaseDetail.utils';
import { Roles } from '@cp/pages/auth/constants/AuthConstants';
import { Due } from '../../interfaces/CaseDetail.type';

const EmiCalendarActionCell = ({
  pendingFeeComponents,
  rowData,
  isExpanded,
  toggleWaiveFeeDrawer,
  toggleWaiveFeeDrawerV2,
  setFeeComponentAdjustmentsForDue,
  handleReferenceId,
  setDueDetails,
  setFlowType,
  editAccessFlag = false
}: EmiCalendarActionCellProps) => {
  const featureFlags = useSelector((store: RootState) => store?.common?.featureFlags);
  const user = useSelector((store: RootState) => store.common.userData);
  const isGlobalAccessGiven = user?.roles?.includes(Roles.ROLE_GLOBAL_ACCESS);
  const { loanId = '', customerId = '' } = useParams();

  const { feeWaiverFeatureFlag } = featureFlags || {};
  const { action, referenceId, showHoldTooltip, disableHoldBtn } = (rowData as Due) || {};
  const isPartPrePaymentType = rowData?.type === STATUS.PART_PRE_PAYMENT;
  const waiveFeeClickHandler = (e: MouseEvent) => {
    e?.stopPropagation();
    toggleWaiveFeeDrawer(true);
    setDueDetails(rowData as Due);
    setFeeComponentAdjustmentsForDue(componentAdjustmentsList);
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_EMISchedule_WaiveFeeCTA);
  };

  const waiveFeeClickHandlerV2 = (e: MouseEvent) => {
    e?.stopPropagation();
    addClickstreamEvent(FEE_WAIVER_CLICKSTREAM.LH_WAIVE_BUTTON_CLICKED, {
      emiReferenceId: referenceId,
      loanAccountNumber: loanId,
      action: EmiFeeActions[action || EmiFeeActions.HOLD]
    });
    toggleWaiveFeeDrawerV2(true);
    setDueDetails(rowData as Due);
  };

  const componentAdjustmentsList = useMemo(() => {
    if (!pendingFeeComponents) {
      return [];
    }
    const { data = [] } = pendingFeeComponents;

    const feeComponents = data?.filter(
      ({ dueReferenceId }) => dueReferenceId === rowData.referenceId
    );

    const feeDetails = feeComponents?.[0]?.feeDetails;

    if (!feeDetails) {
      return [];
    }

    const validFeeComponents = feeDetails.filter(({ feeType }) => !!WaiveFeeType[feeType]);
    setFlowType(prevState => {
      return validFeeComponents.some(feeComponent => feeComponent.feeType === 'PENALTY_CHARGES')
        ? { ...prevState, [rowData.referenceId]: EmiFlowType.NEW_FLOW }
        : { ...prevState, [rowData.referenceId]: EmiFlowType.OLD_FLOW };
    });

    const feeComponentTotal = validFeeComponents?.reduce(
      (component, pendingFeeComponent) => ({
        ...component,
        totalOriginalFeeAmount:
          component.totalOriginalFeeAmount + pendingFeeComponent.totalOriginalFeeAmount,
        totalOriginalFeeGst:
          component.totalOriginalFeeGst + pendingFeeComponent.totalOriginalFeeGst,
        totalRevisedFeeAmount:
          component.totalRevisedFeeAmount + pendingFeeComponent.totalRevisedFeeAmount,
        totalRevisedFeeGst: component.totalRevisedFeeGst + pendingFeeComponent.totalRevisedFeeGst,
        totalAmountPending: component.totalAmountPending + pendingFeeComponent.totalAmountPending,
        totalGstPending: component.totalGstPending + pendingFeeComponent.totalGstPending,
        totalWaivedAmount: component.totalWaivedAmount + pendingFeeComponent.totalWaivedAmount,
        totalWaivedGst: component.totalWaivedGst + pendingFeeComponent.totalWaivedGst,
        totalDeferredAmount:
          component.totalDeferredAmount + pendingFeeComponent.totalDeferredAmount,
        totalDeferredGst: component.totalDeferredGst + pendingFeeComponent.totalDeferredGst,
        totalHeldAmount: component.totalHeldAmount + pendingFeeComponent.totalHeldAmount,
        totalHeldGst: component.totalHeldGst + pendingFeeComponent.totalHeldGst
      }),
      {
        feeType: 'PENALTY_CHARGES',
        totalOriginalFeeAmount: 0,
        totalOriginalFeeGst: 0,
        totalRevisedFeeAmount: 0,
        totalRevisedFeeGst: 0,
        totalAmountPending: 0,
        totalGstPending: 0,
        totalWaivedAmount: 0,
        totalWaivedGst: 0,
        totalDeferredAmount: 0,
        totalDeferredGst: 0,
        totalHeldAmount: 0,
        totalHeldGst: 0
      }
    );

    return [feeComponentTotal];
  }, [pendingFeeComponents]);

  const handleOverlay = (rowData: any, type: string) => (event: MouseEvent) => {
    event.stopPropagation();
    if (rowData?.paid) {
      handleReferenceId(rowData.referenceId || rowData.referenceScheduleId, type);
    }
    if (isPartPrePaymentType) {
      handleReferenceId(rowData?.referenceId, type);
    }
  };

  const disableHoldOrWaiveBtn = !action || disableHoldBtn;
  const disableCTAs = isGlobalAccessGiven
    ? editAccessFlag && disableHoldOrWaiveBtn
    : disableHoldOrWaiveBtn;
  const isDisabled = feeWaiverFeatureFlag ? disableCTAs : componentAdjustmentsList.length === 0;
  const handleFeeActionClick = (ref: HTMLButtonElement | null) => {
    if (!ref) return;
    ref.onclick = feeWaiverFeatureFlag ? waiveFeeClickHandlerV2 : waiveFeeClickHandler;
  };
  return (
    <div className={styles['emiCalendar__colDefs__actionCellRenderer']}>
      {isPartPrePaymentType ? (
        <div
          className={cx(
            styles['emiCalendar__colDefs__actionCellRenderer__actions'],
            'w-[80px] justify-start'
          )}
        >
          --
        </div>
      ) : (
        <Tooltip placement="top" hiddenPadding={0} hideStrategy="referenceHidden">
          <TooltipTrigger tooltipTriggerClassName="tooltipTriggerWrapper">
            <Button
              variant="text"
              size="medium"
              disabled={isDisabled}
              className={cx(
                styles['emiCalendar__colDefs__actionCellRenderer__actions'],
                'w-[80px] justify-start'
              )}
              ref={handleFeeActionClick}
            >
              {feeWaiverFeatureFlag
                ? EmiFeeActionLabelMap[action || EmiFeeActions.HOLD]
                : EMI_CALENDAR_ACTIONS.WAIVE_FEE.label}
            </Button>
          </TooltipTrigger>
          {feeWaiverFeatureFlag && showHoldTooltip ? (
            <TooltipContent className="tooltipWrapper max-w-[280px]">
              You can waive penalty of this overdue once you have waive penalty of previous overdue
            </TooltipContent>
          ) : null}
        </Tooltip>
      )}

      {/* TODO: Remove EMI breakup action from all places */}
      {/* <Button
        variant="text"
        size="medium"
        className={styles['emiCalendar__colDefs__actionCellRenderer__actions']}
        onClick={handleOverlay(rowData, EMI_CALENDAR_ACTIONS.EMI_BREAKUP.value)}
      >
        {EMI_CALENDAR_ACTIONS.EMI_BREAKUP.label}
      </Button> */}
      <div className={styles['emiCalendar__colDefs__actionCellRenderer__actions']}>
        <div
          ref={ref => {
            if (!ref) return;
            ref.onclick = handleOverlay(rowData, EMI_CALENDAR_ACTIONS.REPAYMENT_DUE.value);
          }}
        >
          <PaymentHistoryIcon
            fill={
              rowData?.paid || isPartPrePaymentType
                ? PAYMENT_HISTORY_ICON_FILL[STATUS.PAID]
                : PAYMENT_HISTORY_ICON_FILL[STATUS.UNPAID]
            }
          />
        </div>
      </div>
      <ArrowUpSolidIcon
        width={10}
        className={cx(styles.arrowIcon, { [styles.rotateIcon]: !isExpanded })}
      />
    </div>
  );
};

export default EmiCalendarActionCell;
